import { useRef, useEffect } from 'react';
import { isMobile, isIOS } from 'react-device-detect';
import {
  AiOutlinePrinter,
  AiOutlineSend,
  AiOutlineWhatsApp,
} from 'react-icons/ai';
import { BsFiles, BsFillTrashFill } from 'react-icons/bs';
import { useReactToPrint } from 'react-to-print';
import { Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import BudgetPrint from '../../../components/BudgetPrint/BudgetPrint';
import { DefaultButton } from '../../../components/Button/Button';
import { LogoMissingNotice } from '../../../components/LogoMissingNotice/LogoMissingNotice';
import { useLogoPreloader } from '../../../core/hooks/useLogoPreloader';
import { useImagesPreloader } from '../../../core/hooks/useImagesPreloader';
import { saveBudget } from '../../../core/utils/budget';
import { waitForFontsReady } from '../../../core/utils/waitForFontsReady';
import {
  buildPdfBlobFromNode,
  deliverPdfBlob,
} from '../../../core/utils/generatePdfWithImages';
import './styles.scss';

export const ActionsButton = (props) => {
  const {
    sendWhatsapp,
    toShare,
    isLoading,
    values,
    onRemove,
    onClone,
    compareValues,
    onSave,
    reloadPrint,
    printCompany,
    client,
    totalSum,
    totalBodyshopService,
    totalParts,
    totalAeService,
    bodyshopService,
    aeService,
    parts,
    observation,
  } = props;

  if (values && !values.id) {
    return;
  }

  const printRef = useRef();
  const timeoutsRef = useRef([]);
  const originalTitle = document.title;
  const { logoBase64, waitForReady: waitForLogoReady } = useLogoPreloader(
    printCompany?.logo
  );
  const checklistImagePaths = (values?.image_checklist || [])
    .map((item) => item?.image)
    .filter(Boolean);
  const { imagesBase64 } = useImagesPreloader(checklistImagePaths);

  // Cleanup de timeouts ao desmontar componente
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
  }, []);

  // So o iOS precisa do html2canvas (react-to-print sai sem as fotos no iPhone).
  // Android e desktop seguem no react-to-print, que funciona.
  const generateMobilePdf = async () => {
    if (!printRef.current) return;
    const sanitize = (value) => (value || '').replace(/[\s\\/]/g, '_');
    const filename = `Orcamento_${values.code}_${sanitize(
      values.client_name
    )}_${sanitize(values.vehicle_name)}.pdf`;
    Swal.fire({
      title: 'Gerando PDF...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      const blob = await buildPdfBlobFromNode(printRef.current);
      Swal.close();
      await deliverPdfBlob(blob, filename);
      if (values?.shared_at === null) {
        await saveBudget(values.id, { shared_at: new Date() });
      }
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      Swal.fire({
        icon: 'error',
        title: 'Não foi possível gerar o PDF',
        text: 'Tente novamente.',
      });
    }
  };

  const runOutput = () => (isIOS ? generateMobilePdf() : handlePrint());

  const toPrint = () => {
    if (!compareValues) {
      const timeout = setTimeout(() => {
        runOutput();
      }, 500);
      timeoutsRef.current.push(timeout);
    } else {
      Swal.fire({
        title: 'Salve o conteúdo para poder imprimir o PDF.',
        showCancelButton: true,
        showConfirmButton: true,
        showDenyButton: false,
        icon: 'warning',
        confirmButtonText: `Salvar`,
        cancelButtonText: 'Cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          const timeout = setTimeout(() => {
            onSave(runOutput);
          }, 500);
          timeoutsRef.current.push(timeout);
        } else {
          history.push('/budgets');
        }
      });
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,

    onBeforeGetContent: async () => {
      const newTitle = `Orcamento_${values.code}_${values.client_name}_${values.vehicle_name}}`;
      document.title = newTitle;
      // Sem logo+fontes prontos, o print do iframe sai com logo gigante ou fonte trocada.
      await Promise.all([waitForLogoReady(4000), waitForFontsReady(3000)]);
    },

    onAfterPrint: () => {
      document.title = originalTitle;
    },

    onBeforePrint: async () => {
      const budgetId = values.id;
      if (values?.shared_at === null) {
        await saveBudget(budgetId, {
          shared_at: new Date(),
        });
      }
    },
  });

  return (
    <>
      <BudgetPrint
        bodyshopService={bodyshopService}
        aeService={aeService}
        parts={parts}
        ref={printRef}
        reloadPrint={reloadPrint}
        company={printCompany}
        logoBase64={logoBase64}
        imagesBase64={imagesBase64}
        client={client}
        totalSum={totalSum}
        totalBodyshopService={totalBodyshopService}
        totalParts={totalParts}
        values={values}
        isLoading={isLoading}
        totalServices={totalBodyshopService + totalAeService}
        totalAeService={totalAeService}
        observation={observation}
      />

      <div style={{ marginBottom: 16 }}>
        {!printCompany?.logo && (
          <LogoMissingNotice companyId={printCompany?.id} />
        )}
        <div
          className="header-title budget-action-button-container"
          style={{ gap: isMobile ? 10 : 20 }}
        >
          {isMobile ? (
            <>
              <div
                className="mt-2-5_d-flex"
                style={{ flex: '0 0 100%', gap: 10, width: '100%' }}
              >
                <DefaultButton
                  className="modal-print modal-footer-button cancel margin-0"
                  label={'IMPRIMIR'}
                  icon={<AiOutlinePrinter className="button-icon-cancel" />}
                  onClick={toPrint}
                  style={{ flex: 1 }}
                />
                <DefaultButton
                  className="modal-footer-button cancel margin-0"
                  label={'ENVIAR'}
                  icon={<AiOutlineWhatsApp className="button-icon-cancel" />}
                  onClick={sendWhatsapp}
                  style={{ flex: 1 }}
                />
              </div>
              <DefaultButton
                className="modal-footer-button cancel margin-0"
                label={'COMPARTILHAR'}
                icon={<AiOutlineSend className="button-icon-cancel" />}
                onClick={toShare}
                disabled={values && isLoading}
                style={{ flex: 1 }}
              />
            </>
          ) : (
            <>
              <DefaultButton
                className="modal-print modal-footer-button cancel margin-0"
                label={'IMPRIMIR'}
                icon={<AiOutlinePrinter className="button-icon-cancel" />}
                onClick={toPrint}
              />
              <DefaultButton
                className="modal-footer-button cancel margin-0"
                label={'ENVIAR'}
                icon={<AiOutlineWhatsApp className="button-icon-cancel" />}
                onClick={sendWhatsapp}
              />
              <DefaultButton
                className="modal-footer-button cancel margin-0"
                label={'COMPARTILHAR'}
                icon={<AiOutlineSend className="button-icon-cancel" />}
                onClick={toShare}
                disabled={values && isLoading}
              />
            </>
          )}
          {isLoading && (
            <div className="spinner-clear">
              <Spinner color="primary" size="sm" />
            </div>
          )}
          <DefaultButton
            className="modal-footer-button cancel margin-0"
            label={'CLONAR'}
            icon={<BsFiles className="button-icon-cancel" />}
            onClick={onClone}
            disabled={values && isLoading}
          />
          <DefaultButton
            className="modal-footer-button delete"
            label={'Excluir'}
            icon={<BsFillTrashFill className="button-icon-cancel" />}
            onClick={onRemove}
          />
        </div>
      </div>
    </>
  );
};
